import type {
  WebABIteration,
  WebABIterationPublishInput,
  WebABIterationVariantSnapshot,
  WebABAllocationMode,
  WebABRoutingConfig,
  WebABVariant,
  WebABVariantRole,
  WebPlacement,
} from '@modules/webplacements/types/webplacements.types';
import {
  buildWebABIterationId,
  buildWebABRoutingHash as buildSharedWebABRoutingHash,
  getWebABAllocationMode as getSharedWebABAllocationMode,
  normalizeWebABRoutingState as normalizeSharedWebABRoutingState,
} from '@modules/shared-types';
const WEB_AB_VARIANT_ROLES = ['baseline', 'control', 'treatment', 'holdout'] as const;

const getDefaultWebABVariantRole = (index: number): WebABVariantRole =>
  index === 0 ? 'baseline' : 'treatment';

const isWebABVariantRole = (value: unknown): value is WebABVariantRole =>
  typeof value === 'string' && (WEB_AB_VARIANT_ROLES as readonly string[]).includes(value);

interface BuildIterationParams {
  webPlacement: WebPlacement;
  abSalt: string;
  publishedAt: string;
  publishedBy: string;
  publishInput?: WebABIterationPublishInput;
}

const cleanText = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

export const normalizeWebABTags = (tags: string[] | undefined): string[] | undefined => {
  if (!tags?.length) return undefined;

  const normalized = Array.from(new Set(
    tags
      .map(tag => tag.trim())
      .filter(Boolean)
  ));

  return normalized.length > 0 ? normalized : undefined;
};

export const normalizeWebABVariantRole = (
  role: unknown,
  index: number
): WebABVariantRole => {
  if (isWebABVariantRole(role)) return role;
  return getDefaultWebABVariantRole(index);
};

export const buildWebABRoutingHash = (
  abSalt: string,
  variants: WebABVariant[]
): string => buildSharedWebABRoutingHash(abSalt, variants);

/**
 * Normalize only routing-critical state. Human-readable labels and operator
 * metadata intentionally stay out of the hash so a metadata-only republish is
 * idempotent and keeps the current allocation epoch.
 */
export const normalizeWebABRoutingState = (
  abSalt: string,
  variants: WebABVariant[]
) => normalizeSharedWebABRoutingState(abSalt, variants);

export const getWebABAllocationMode = (
  variants: Array<Pick<WebABVariant, 'weight'>>
): WebABAllocationMode => getSharedWebABAllocationMode(variants);

export const buildWebABIterationSnapshot = ({
  webPlacement,
  abSalt,
  publishedAt,
  publishedBy,
  publishInput,
}: BuildIterationParams): WebABIteration => {
  const current = webPlacement.abCurrentIteration;
  const routingHash = buildWebABRoutingHash(abSalt, webPlacement.variants || []);
  const reuseCurrent = !!current && current.routingHash === routingHash;

  // A no-op republish must preserve the complete immutable snapshot, including
  // its original activation timestamp and publisher. The legacy UI mode is a
  // hint only: routing-critical changes always roll over automatically, while
  // an unchanged routing hash never creates history noise.
  if (reuseCurrent) {
    return current;
  }

  const sequence = Math.max(
    webPlacement.abIterationSequence || 0,
    current?.sequence || 0
  ) + 1;

  const variants: WebABIterationVariantSnapshot[] = (webPlacement.variants || []).map((variant, index) => ({
    id: variant.id,
    name: variant.name,
    ...(cleanText(variant.description) ? { description: cleanText(variant.description) } : {}),
    role: normalizeWebABVariantRole(variant.role, index),
    ...(normalizeWebABTags(variant.tags) ? { tags: normalizeWebABTags(variant.tags) } : {}),
    weight: variant.weight,
    webPlacementId: variant.webPlacementId,
    webPlacementAlias: variant.webPlacementAlias,
    webPlacementName: variant.webPlacementName,
  }));

  return {
    id: buildWebABIterationId(sequence, routingHash),
    sequence,
    name: cleanText(publishInput?.name) || `Iteration ${sequence}`,
    ...(cleanText(publishInput?.description) ? { description: cleanText(publishInput?.description) } : {}),
    ...(normalizeWebABTags(publishInput?.tags) ? { tags: normalizeWebABTags(publishInput?.tags) } : {}),
    ...(cleanText(publishInput?.changeReason) ? { changeReason: cleanText(publishInput?.changeReason) } : {}),
    status: 'published',
    publishedAt,
    publishedBy,
    abSalt,
    routingHash,
    allocationMode: getWebABAllocationMode(webPlacement.variants || []),
    variants,
  };
};

export const buildWebABRoutingConfig = (
  webPlacement: WebPlacement,
  iteration: WebABIteration
): WebABRoutingConfig => ({
  abSalt: iteration.abSalt,
  abTestId: webPlacement.id,
  abTestName: webPlacement.name,
  abTestIterationId: iteration.id,
  routingHash: iteration.routingHash,
  allocationMode: iteration.allocationMode || getWebABAllocationMode(iteration.variants),
  ...(iteration.tags?.length ? { abTestIterationTags: iteration.tags } : {}),
  variants: iteration.variants.map(variant => ({
    id: variant.id,
    name: variant.name,
    weight: variant.weight,
    targetAlias: variant.webPlacementAlias,
    ...(variant.tags?.length ? { tags: variant.tags } : {}),
  })),
});
